import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Pencil, X } from 'lucide-react';
import Button from '../../../shared/components/ui/Button';
import AiLanguageSelect, { languageDisplay } from './AiLanguageSelect';
import {
  buildScheduleDateOptions,
  buildScheduleTimeSlots,
  toScheduledAtIso
} from './aiScheduleTime';

function WhatsAppPhonePreview({ recipientName, message }) {
  return (
    <div className="mx-auto mb-4 w-full max-w-[260px]">
      <div className="overflow-hidden rounded-[2rem] border-4 border-gray-800 bg-[#e5ddd5] shadow-lg">
        <div className="bg-[#075E54] px-4 py-3">
          <p className="text-sm font-semibold text-white">{recipientName || 'Lead'}</p>
          <p className="text-[10px] text-emerald-100">online</p>
        </div>
        <div className="min-h-[220px] space-y-2 px-3 py-4">
          <div className="ml-auto max-w-[92%] rounded-lg rounded-tr-none bg-[#DCF8C6] px-3 py-2 text-xs leading-relaxed text-gray-900 shadow-sm">
            {message?.trim() ? message : 'Your AI-generated message will appear here…'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AiWhatsAppDrawer({
  open,
  onClose,
  recipientName,
  recipientPhone,
  tone,
  templateKey,
  recommendedTemplateKey,
  templateKeyMismatchWarning,
  toneOptions,
  templateOptions,
  message,
  sessionOpen = true,
  sessionBlockReason,
  sessionUserMessage,
  sessionHint,
  pendingTemplateNote,
  canSendAsTemplate = false,
  approvedPinggoTemplates = [],
  selectedMetaTemplateName = '',
  onMetaTemplateChange,
  lastCustomerReplyAt,
  hoursSinceCustomerReply,
  onToneChange,
  onTemplateChange,
  onMessageChange,
  onRegenerate,
  onSend,
  onSendTemplate,
  onSchedule,
  scheduleSuggestion = null,
  initialScheduleMode = false,
  isScheduling = false,
  targetLanguage = 'en',
  onTargetLanguageChange,
  translationLanguages,
  showTranslation = true,
  originalEnglishMessage = '',
  onTranslate,
  isTranslating = false,
  isRegenerating,
  isSending
}) {
  const [editing, setEditing] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const timeSlots = useMemo(() => buildScheduleTimeSlots(), []);
  const dateOptions = useMemo(() => buildScheduleDateOptions(90), []);

  const initialSchedule = useMemo(() => {
    if (scheduleSuggestion?.suggestedDate && scheduleSuggestion?.suggestedTime) {
      return {
        date: scheduleSuggestion.suggestedDate,
        time: scheduleSuggestion.suggestedTime
      };
    }
    return { date: dateOptions[0], time: '11:00' };
  }, [scheduleSuggestion, dateOptions]);

  const [scheduleDate, setScheduleDate] = useState(initialSchedule.date);
  const [scheduleTime, setScheduleTime] = useState(initialSchedule.time);

  useEffect(() => {
    if (!open) {
      setEditing(false);
      setScheduleOpen(false);
      return;
    }
    setScheduleOpen(!!initialScheduleMode);
    setScheduleDate(initialSchedule.date);
    setScheduleTime(initialSchedule.time);
    if (!sessionOpen && canSendAsTemplate) {
      setEditing(true);
    }
  }, [open, initialScheduleMode, initialSchedule.date, initialSchedule.time, sessionOpen, canSendAsTemplate]);

  if (!open) return null;

  const charCount = message?.length || 0;
  const smsEquivalent = Math.ceil(charCount / 160) || 1;
  const scheduledAtIso = toScheduledAtIso(scheduleDate, scheduleTime);

  const handleSchedule = () => {
    onSchedule?.({
      scheduledAt: scheduledAtIso,
      sendMode: !sessionOpen && canSendAsTemplate ? 'template' : 'text',
      metaTemplateName: selectedMetaTemplateName || undefined
    });
  };

  return (
    <div className="fixed inset-0 z-[1200] flex justify-end bg-black/30">
      <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        {isRegenerating ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
            <p className="text-sm font-medium text-[#1E4A53]">Generating message…</p>
          </div>
        ) : null}
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">WhatsApp Message</h3>
            <p className="text-xs text-gray-500">Generated from AI Insights</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <WhatsAppPhonePreview recipientName={recipientName} message={message} />

          <div className="mb-4 rounded-xl bg-gray-50 p-3">
            <p className="text-sm font-semibold text-gray-900">{recipientName || 'Lead'}</p>
            {recipientPhone ? (
              <p className="text-sm text-teal-700">{recipientPhone}</p>
            ) : null}
          </div>

          {!sessionOpen ? (
            <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-3 py-3 text-xs leading-relaxed text-amber-950">
              <p className="font-semibold">Template message required</p>
              <p className="mt-1">
                {sessionUserMessage ||
                  sessionBlockReason ||
                  "This lead hasn't replied on WhatsApp recently, so only approved templates can be sent."}
              </p>
              {lastCustomerReplyAt ? (
                <p className="mt-2 text-amber-800">
                  Last customer reply: {new Date(lastCustomerReplyAt).toLocaleString()}
                  {hoursSinceCustomerReply != null ? ` (${hoursSinceCustomerReply}h ago)` : ''}
                </p>
              ) : (
                <p className="mt-2 text-amber-800">No reply from this lead on WhatsApp yet.</p>
              )}
              {pendingTemplateNote ? (
                <p className="mt-2 rounded-lg border border-amber-200 bg-white/60 px-2 py-2 text-amber-900">
                  {pendingTemplateNote}
                </p>
              ) : null}
              <p className="mt-2">
                {canSendAsTemplate
                  ? 'Pick an approved template below. Your AI draft fills variable slots where the template allows.'
                  : sessionHint ||
                    'No approved WhatsApp templates yet. Ask your admin to approve one in Pinggo.'}
              </p>
            </div>
          ) : null}

          {!sessionOpen && canSendAsTemplate && approvedPinggoTemplates.length > 0 ? (
            <div className="mb-4">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Pinggo template (first contact)
              </label>
              <select
                value={selectedMetaTemplateName}
                onChange={(e) => onMetaTemplateChange?.(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                {approvedPinggoTemplates.map((t) => (
                  <option key={t.templateName} value={t.templateName}>
                    {t.templateName.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {showTranslation ? (
            <>
              <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                <AiLanguageSelect
                  value={targetLanguage}
                  onChange={onTargetLanguageChange}
                  disabled={isTranslating || isRegenerating}
                  languages={translationLanguages}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="h-[42px] whitespace-nowrap"
                  disabled={
                    isTranslating ||
                    isRegenerating ||
                    targetLanguage === 'en' ||
                    !originalEnglishMessage?.trim()
                  }
                  onClick={onTranslate}>
                  {isTranslating ? 'Translating…' : 'Translate'}
                </Button>
              </div>

              {targetLanguage !== 'en' && originalEnglishMessage?.trim() ? (
                <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600">
                  <p className="mb-1 font-semibold uppercase tracking-wide text-gray-500">
                    English original
                  </p>
                  <p>{originalEnglishMessage}</p>
                </div>
              ) : null}

              {targetLanguage !== 'en' ? (
                <p className="mb-3 text-[11px] text-teal-800">
                  Preview & send in {languageDisplay(targetLanguage)}. English is kept on file.
                </p>
              ) : null}
            </>
          ) : null}

          <div className="mb-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Tone</p>
            <div className="flex flex-wrap gap-2">
              {toneOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => onToneChange(option.id)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                    tone === option.id
                      ? 'bg-[#1E4A53] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Message template
            </label>
            <select
              value={templateKey}
              onChange={(e) => onTemplateChange(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {templateOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                  {option.id === recommendedTemplateKey ? ' (recommended)' : ''}
                </option>
              ))}
            </select>
            {recommendedTemplateKey && templateKey === recommendedTemplateKey ? (
              <p className="mt-1 text-[11px] text-teal-700">
                Matches AI insight recommendation for this lead.
              </p>
            ) : null}
            {templateKeyMismatchWarning ? (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-2 text-[11px] leading-relaxed text-amber-950">
                {templateKeyMismatchWarning}
              </p>
            ) : null}
          </div>

          {editing ? (
            <div className="mb-2">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Edit Message
              </label>
              <textarea
                value={message}
                onChange={(e) => onMessageChange(e.target.value)}
                rows={8}
                disabled={isRegenerating}
                className="w-full rounded-xl border border-gray-300 px-3 py-3 text-sm leading-relaxed disabled:bg-gray-50"
              />
              <p className="mt-1 text-xs text-gray-500">
                {charCount} characters · {smsEquivalent} SMS equivalent
              </p>
            </div>
          ) : null}

          {scheduleOpen ? (
            <div className="mb-4 rounded-xl border border-teal-100 bg-teal-50/40 p-3">
              {scheduleSuggestion?.reason ? (
                <div className="mb-3 flex gap-2 text-xs leading-relaxed text-teal-950">
                  <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4A53]" />
                  <span>{scheduleSuggestion.reason}</span>
                </div>
              ) : null}
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Schedule for later
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-[10px] font-semibold uppercase text-gray-500">
                    Date
                  </label>
                  <select
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                    {dateOptions.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-semibold uppercase text-gray-500">
                    Time (11 AM – 5 PM)
                  </label>
                  <select
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="mt-2 text-[11px] text-gray-600">
                Sends automatically at {scheduleDate} {scheduleTime} IST.
              </p>
            </div>
          ) : null}

          <p className="text-xs text-gray-500">
            Messages logged in Sales Log automatically with delivery status.
          </p>
        </div>

        <div className="border-t px-5 py-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              disabled={isRegenerating}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#1E4A53]/30 bg-white px-4 py-2.5 text-sm font-semibold text-[#1E4A53] hover:bg-teal-50 disabled:opacity-60">
              <Pencil className="h-3.5 w-3.5" />
              {editing ? 'Hide editor' : 'Edit message'}
            </button>
            <button
              type="button"
              onClick={onRegenerate}
              disabled={isRegenerating}
              className="inline-flex w-full items-center justify-center rounded-lg border border-[#1E4A53]/30 bg-white px-4 py-2.5 text-sm font-semibold text-[#1E4A53] hover:bg-teal-50 disabled:opacity-60">
              {isRegenerating ? 'Regenerating…' : 'Regenerate'}
            </button>

            {sessionOpen ? (
              <button
                type="button"
                onClick={onSend}
                disabled={isSending || isScheduling || !message?.trim()}
                className="inline-flex w-full items-center justify-center rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1fb855] disabled:opacity-60">
                {isSending ? 'Sending…' : 'Send now'}
              </button>
            ) : canSendAsTemplate ? (
              <button
                type="button"
                onClick={onSendTemplate}
                disabled={isSending || isScheduling || !selectedMetaTemplateName}
                className="inline-flex w-full items-center justify-center rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1fb855] disabled:opacity-60">
                {isSending ? 'Sending…' : 'Send now'}
              </button>
            ) : (
              <button
                type="button"
                onClick={onSend}
                disabled
                className="inline-flex w-full items-center justify-center rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white opacity-60">
                Send now
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                if (scheduleOpen) {
                  handleSchedule();
                } else {
                  setScheduleOpen(true);
                }
              }}
              disabled={isScheduling || isSending || !message?.trim() || !onSchedule}
              className="inline-flex w-full items-center justify-center rounded-lg border border-[#1E4A53] bg-white px-4 py-2.5 text-sm font-semibold text-[#1E4A53] hover:bg-teal-50 disabled:opacity-60">
              {isScheduling ? 'Scheduling…' : scheduleOpen ? 'Confirm schedule' : 'Schedule send'}
            </button>
          </div>

          {scheduleOpen ? (
            <button
              type="button"
              onClick={() => setScheduleOpen(false)}
              className="mt-2 w-full text-center text-xs text-gray-500 hover:text-gray-700">
              Cancel scheduling
            </button>
          ) : null}

          {!sessionOpen && canSendAsTemplate ? (
            <p className="mt-3 text-[11px] leading-relaxed text-gray-500">
              Sends via Pinggo: {selectedMetaTemplateName || '—'}. Templates with variables use your
              AI draft; static templates send fixed approved text.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
